import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";
import api from "../config/axios";

function Invites() {
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingInvite, setEditingInvite] = useState(null);
  const [formData, setFormData] = useState({
    nom: "",
    url: "",
    genre: "H",
    is_active: true,
    order_index: 0,
  });

  const genres = [
    { value: "H", label: "Homme" },
    { value: "F", label: "Femme" },
  ];

  useEffect(() => {
    fetchInvites();
  }, []);

  const fetchInvites = async () => {
    try {
      const response = await api.get("/api/invites");
      const invitesWithBoolean = response.data
        .map((invite) => ({
          ...invite,
          is_active: Boolean(invite.is_active),
        }))
        .sort((a, b) => a.order_index - b.order_index || a.id - b.id);
      setInvites(invitesWithBoolean);
    } catch (error) {
      console.error("Erreur lors de la récupération des invités:", error);
    }
  };

  const handleOpen = (invite = null) => {
    if (invite) {
      setEditingInvite(invite);
      setFormData({
        nom: invite.nom,
        url: invite.url,
        genre: invite.genre,
        is_active: Boolean(invite.is_active),
        order_index: invite.order_index || 0,
      });
    } else {
      setEditingInvite(null);
      setFormData({
        nom: "",
        url: "",
        genre: "H",
        is_active: true,
        order_index: 0,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingInvite(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingInvite) {
        await api.put(`/api/invites/${editingInvite.id}`, formData);
      } else {
        await api.post("/api/invites", formData);
      }
      fetchInvites();
      handleClose();
    } catch (error) {
      console.error("Erreur lors de la sauvegarde de l'invité:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cet invité ?")) {
      try {
        await api.delete(`/api/invites/${id}`);
        fetchInvites();
      } catch (error) {
        console.error("Erreur lors de la suppression de l'invité:", error);
      }
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await api.put(`/api/invites/${id}`, {
        is_active: !Boolean(currentStatus),
      });
      fetchInvites();
    } catch (error) {
      console.error("Erreur lors de la mise à jour du statut:", error);
    }
  };

  const moveInvite = async (index, direction) => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === invites.length - 1)
    ) {
      return;
    }

    const newIndex = direction === "up" ? index - 1 : index + 1;
    const updatedInvites = [...invites];
    const temp = updatedInvites[index];
    updatedInvites[index] = updatedInvites[newIndex];
    updatedInvites[newIndex] = temp;

    const updates = updatedInvites.map((invite, idx) => ({
      id: invite.id,
      order_index: idx,
    }));

    try {
      await api.put("/api/invites/reorder", { updates });
      setInvites(
        updatedInvites.map((invite, idx) => ({
          ...invite,
          order_index: idx,
        }))
      );
    } catch (error) {
      console.error("Erreur lors de la mise a jour de l'ordre:", error);
      fetchInvites();
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4">Invités</Typography>
        <Button variant="contained" onClick={() => handleOpen()}>
          Nouvel invité
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Ordre</TableCell>
              <TableCell>Commande</TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>URL</TableCell>
              <TableCell>Genre</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invites.map((invite, index) => (
              <TableRow key={invite.id}>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => moveInvite(index, "up")}
                    disabled={index === 0}
                  >
                    <ArrowUpward fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => moveInvite(index, "down")}
                    disabled={index === invites.length - 1}
                  >
                    <ArrowDownward fontSize="inherit" />
                  </IconButton>
                </TableCell>
                <TableCell>!invite{index + 1}</TableCell>
                <TableCell>{invite.nom}</TableCell>
                <TableCell>
                  <a
                    href={invite.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {invite.url}
                  </a>
                </TableCell>
                <TableCell>
                  {invite.genre === "H" ? "Homme" : "Femme"}
                </TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={invite.is_active}
                        onChange={() =>
                          handleToggleActive(invite.id, invite.is_active)
                        }
                      />
                    }
                    label={invite.is_active ? "Actif" : "Inactif"}
                  />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpen(invite)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(invite.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>
          {editingInvite ? "Modifier l'invité" : "Nouvel invité"}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Nom"
              value={formData.nom}
              onChange={(e) =>
                setFormData({ ...formData, nom: e.target.value })
              }
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="URL Instagram"
              value={formData.url}
              onChange={(e) =>
                setFormData({ ...formData, url: e.target.value })
              }
              margin="normal"
              required
            />
            <TextField
              fullWidth
              select
              label="Genre"
              value={formData.genre}
              onChange={(e) =>
                setFormData({ ...formData, genre: e.target.value })
              }
              margin="normal"
              required
            >
              {genres.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              type="number"
              label="Ordre"
              value={formData.order_index}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  order_index: parseInt(e.target.value, 10) || 0,
                })
              }
              margin="normal"
              helperText="Plus petit = affiche en premier pour !invite1"
              inputProps={{ min: 0, max: 99 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                />
              }
              label="Actif"
            />
            <DialogActions>
              <Button onClick={handleClose}>Annuler</Button>
              <Button type="submit" variant="contained">
                {editingInvite ? "Modifier" : "Créer"}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Invites;
